import { deleteApp, initializeApp } from 'firebase/app';
import { getDatabase, child, ref, onValue, onDisconnect, serverTimestamp, update } from 'firebase/database';

import { logFirebaseConnection, logger } from '@/common/api/logger';
import {
  FIREBASE_CONNECTION_STATUS_BY_OUTCOME,
  FirebaseConnectionContext,
  FirebaseConnectionStatus,
} from './FirebaseConnection.enum';
import * as SdkConfig from '../PleasedClient/client.config';

import type * as Types from './PleasedChat.types';
import { PleasedInternalError, PleasedError } from './errors';
import {
  FirebaseConnectionMonitor,
  getFirebaseConnectionTimeoutMs,
  setFirebaseConnectionStatus,
} from './FirebaseConnectionMonitor';

/**
 * The FirebaseStore utility is designed to manage Firebase Realtime Database instances in the chat application.
 * It provides methods to initialize, retrieve, and destroy Firebase app instances associated with different chat conversations.
 * This utility encapsulates the process of setting up database references, handling app initialization, and managing app destruction.
 */
export const FirebaseStore = {
  /**
   * @private
   */
  apps: {} as Record<string, Types.FirebaseDatabaseStore>,
  /**
   * @private
   * Stores AI session intervention listeners for cleanup
   */
  aiSessionApps: {} as Record<
    string,
    {
      app: any;
      unsubscribeIntervention: () => void;
      unsubscribeConnection: () => void;
      unsubscribeConnectionStatus: () => void;
      monitor: FirebaseConnectionMonitor;
    }
  >,
  async destroy(chatIdOrStore: number | Types.FirebaseDatabaseStore) {
    const appName = typeof chatIdOrStore === 'object' ? chatIdOrStore.id : chatIdOrStore;
    const store = this.apps[appName];
    if (!store) return true;
    return deleteApp(store.app)
      .then(() => true)
      .catch(() => false);
  },
  async destroyAiSession(aiSessionId: string) {
    const appName = `ai-session-${aiSessionId}`;
    const aiSession = this.aiSessionApps[appName];
    if (!aiSession) return true;

    aiSession.unsubscribeIntervention();
    aiSession.unsubscribeConnection();
    aiSession.unsubscribeConnectionStatus();
    aiSession.monitor.cancel();
    delete this.aiSessionApps[appName];

    return deleteApp(aiSession.app)
      .then(() => true)
      .catch(() => false);
  },
  get(chatId: number, token?: string) {
    const appName = `chat-${chatId}`;
    if (this.apps[appName]) return this.apps[appName];

    if (!token)
      throw new PleasedInternalError(
        PleasedError.FIREBASE_APP_INIT_NO_CONFIG,
        `Firebase app ${chatId} has not been initialized yet.`
      );

    const app = initializeApp(
      {
        apiKey: token,
        authDomain: SdkConfig.get('firebase').auth,
        projectId: SdkConfig.get('firebase').projectId,
        databaseURL: SdkConfig.get('firebase').database,
      },
      appName
    );

    const database = getDatabase(app);

    // databases
    const messages = ref(database, `chat/${chatId}`);
    const closed = ref(database, `endedChats/${chatId}`);
    const metadata = ref(database, `chatMetaData/${chatId}`);
    const typing = child(metadata, 'typingMessage');
    const presence = ref(database, `endUserStatus/${chatId}`);

    const connection = ref(database, '.info/connected');
    const timeOffset = ref(database, '.info/serverTimeOffset');

    const store = { messages, closed, metadata, typing, presence, id: appName, app, util: { connection, timeOffset } };

    this.apps[appName] = store;

    return store;
  },
  /**
   * Creates a Firebase app for AI session intervention monitoring
   */
  setupAiSessionIntervention(aiSessionId: string, token: string, onIntervene: (chatId: number) => void) {
    const appName = `ai-session-${aiSessionId}`;

    // If already exists, return early
    if (this.aiSessionApps[appName]) {
      logger.warn(`AI session ${aiSessionId} already has an intervention listener`);
      return () => this.destroyAiSession(aiSessionId);
    }

    setFirebaseConnectionStatus(FirebaseConnectionStatus.Pending);
    const firebaseConnectionMonitor = new FirebaseConnectionMonitor({
      connectionContext: FirebaseConnectionContext.ChatbotAiIntervention,
      timeoutMs: getFirebaseConnectionTimeoutMs(),
    });
    const unsubscribeFromFirebaseConnectionStatus = firebaseConnectionMonitor.onStatusChange((event) => {
      setFirebaseConnectionStatus(FIREBASE_CONNECTION_STATUS_BY_OUTCOME[event.outcome]);
      void logFirebaseConnection(event);
    });

    try {
      const app = initializeApp(
        {
          apiKey: token,
          authDomain: SdkConfig.get('firebase').auth,
          projectId: SdkConfig.get('firebase').projectId,
          databaseURL: SdkConfig.get('firebase').database,
        },
        appName
      );

      const database = getDatabase(app);
      const interveneRef = ref(database, `aiChatIntervention/${aiSessionId}`);
      const statusRef = ref(database, `aiChatEndUserStatus/${aiSessionId}`);
      const connectionRef = ref(database, '.info/connected');

      // Setup user status presence
      update(statusRef, {
        status: 'online',
        chatId: aiSessionId,
        customerId: 0, // Will be set by backend
        lastStatusUpdateTime: serverTimestamp(),
      });

      // Setup disconnect handler
      onDisconnect(statusRef).update({
        status: 'offline',
        lastStatusUpdateTime: serverTimestamp(),
      });

      const unsubscribeConnection = onValue(
        connectionRef,
        (snapshot) => firebaseConnectionMonitor.observe(snapshot.val() === true),
        (error) => firebaseConnectionMonitor.fail(error)
      );

      // Listen for intervention events
      const unsubscribeIntervention = onValue(interveneRef, (snapshot: any) => {
        if (snapshot.exists() && snapshot.val() !== null) {
          const data = snapshot.val();
          if (data.chatId) {
            onIntervene(data.chatId);
          }
        }
      });

      this.aiSessionApps[appName] = {
        app,
        unsubscribeIntervention,
        unsubscribeConnection,
        unsubscribeConnectionStatus: unsubscribeFromFirebaseConnectionStatus,
        monitor: firebaseConnectionMonitor,
      };

      return () => this.destroyAiSession(aiSessionId);
    } catch (error) {
      firebaseConnectionMonitor.fail(error);
      throw error;
    }
  },
};
